import { INCIDENT_NAMES, formatKg, type IncidentKind } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { currentToken } from '../auth/session.js';
import { request } from '../sync/api.js';

/**
 * Дашборд руководителя: выработка, простои, остатки, люди.
 *
 * Только просмотр и выгрузки — руководитель ничего не меняет.
 */

interface Summary {
  period: { from: string; to: string };
  totalPieces: number;
  bySize: Record<string, number>;
  byDay: Array<{ date: string; total: number; bySize: Record<string, number> }>;
  downtime: Array<{ kind: string; title: string; count: number; minutes: number }>;
  balances: Array<{
    code: string;
    name: string;
    qtyG: string;
    negative: boolean;
    producedG: string;
    consumedG: string;
  }>;
  consumptionPerUnit: {
    pieces: number;
    materials: Array<{ code: string; name: string; consumedG: string; perPieceG: number | null }>;
  };
  personal: Array<{ userId: string; fullName: string; pieces: number; materialsG: number; shifts: number }>;
  reliability: Array<{
    userId: string;
    fullName: string;
    shifts: number;
    plannedAbsences: number;
    unplannedAbsences: number;
    missedReports: number;
  }>;
  missingReports: number;
}

const EXPORTS = [
  { path: 'reports', title: 'Сменные отчёты' },
  { path: 'production', title: 'Выработка и сырьё' },
  { path: 'people', title: 'Сотрудники' },
  { path: 'schedule', title: 'График смен' },
  { path: 'hiring', title: 'Найм' },
];

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

export function DashboardScreen(): JSX.Element {
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setData(
        await request<Summary>(
          `/api/analytics/summary?from=${from}&to=${to}`,
          { method: 'GET' },
          30_000,
        ),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить сводку');
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Выгрузка идёт обычной ссылкой, но токен живёт в локальной базе,
   * поэтому файл забираем запросом и отдаём браузеру уже готовым.
   */
  const download = async (path: string, title: string): Promise<void> => {
    const token = await currentToken();
    const response = await fetch(`/api/analytics/export/${path}?from=${from}&to=${to}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      setError(`Не удалось выгрузить «${title}»`);
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title} ${from} — ${to}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const maxDay = Math.max(1, ...(data?.byDay.map((day) => day.total) ?? [1]));

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Сводка</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-touch">
        <label className="flex flex-col gap-1 text-base text-dim">
          С даты
          <input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="min-h-touch rounded-xl bg-card shadow-card px-3 text-strong"
          />
        </label>
        <label className="flex flex-col gap-1 text-base text-dim">
          По дату
          <input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="min-h-touch rounded-xl bg-card shadow-card px-3 text-strong"
          />
        </label>
        {[
          { title: 'Неделя', days: 7 },
          { title: 'Месяц', days: 30 },
        ].map((preset) => (
          <button
            key={preset.title}
            type="button"
            onClick={() => {
              setFrom(isoDaysAgo(preset.days));
              setTo(new Date().toISOString().slice(0, 10));
            }}
            className="min-h-touch rounded-xl bg-card shadow-card px-5 text-base font-semibold"
          >
            {preset.title}
          </button>
        ))}
      </div>

      {error && <p className="mb-4 text-base text-amber-700">{error}</p>}
      {!data && <p className="text-lg text-dim">Загружаю…</p>}

      {data && (
        <>
          <section className="mb-6 grid gap-touch sm:grid-cols-3">
            <div className="rounded-xl bg-card shadow-card p-5">
              <p className="text-display font-semibold tabular-nums">{data.totalPieces}</p>
              <p className="mt-1 text-base text-dim">плит за период</p>
            </div>
            <div className="rounded-xl bg-card shadow-card p-5">
              <p className="text-display font-semibold tabular-nums">
                {data.downtime.reduce((sum, row) => sum + row.minutes, 0)}
              </p>
              <p className="mt-1 text-base text-dim">минут простоя</p>
            </div>
            <div
              className={`rounded-xl p-5 ${data.missingReports > 0 ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200' : 'bg-card shadow-card'}`}
            >
              <p className="text-display font-semibold tabular-nums">{data.missingReports}</p>
              <p className="mt-1 text-base text-body">смен не сдано</p>
            </div>
          </section>

          <section className="mb-8 rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Выработка по дням</h2>
            {data.byDay.length === 0 ? (
              <p className="text-base text-dim">За период нет отчётов</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.byDay.map((day) => (
                  <li key={day.date} className="flex items-center gap-3">
                    <span className="w-24 shrink-0 text-base text-dim">
                      {day.date.slice(8)}.{day.date.slice(5, 7)}
                    </span>
                    <span className="h-6 rounded bg-sky-600" style={{ width: `${(day.total / maxDay) * 70}%` }} />
                    <span className="text-base tabular-nums">{day.total}</span>
                  </li>
                ))}
              </ul>
            )}
            <dl className="mt-4 grid grid-cols-2 gap-y-1 text-base">
              {Object.entries(data.bySize).map(([size, qty]) => (
                <div key={size} className="contents">
                  <dt className="text-dim">{size}</dt>
                  <dd className="tabular-nums">{qty} шт</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mb-8 rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Простои</h2>
            {data.downtime.length === 0 ? (
              <p className="text-base text-dim">Простоев не отмечено</p>
            ) : (
              <ul className="flex flex-col gap-2 text-base">
                {data.downtime.map((row) => (
                  <li key={row.kind} className="flex justify-between">
                    <span className="text-body">
                      {INCIDENT_NAMES[row.kind as IncidentKind] ?? row.title}
                    </span>
                    <span className="tabular-nums">
                      {row.minutes} мин · {row.count} раз
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mb-8 rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Остатки сырья</h2>
            <ul className="flex flex-col gap-2 text-base">
              {data.balances.map((row) => (
                <li
                  key={row.code}
                  className={`flex justify-between rounded-lg px-3 py-2 ${row.negative ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200' : ''}`}
                >
                  <span className="text-body">{row.name}</span>
                  <span className="tabular-nums">
                    {formatKg(Number(row.qtyG))} кг
                    {row.negative && ' — в минусе'}
                  </span>
                </li>
              ))}
            </ul>
            {data.balances.some((row) => row.negative) && (
              <p className="mt-3 text-base text-amber-700">
                Минус означает, что где-то не сдан отчёт или ошиблись в цифрах.
              </p>
            )}
          </section>

          <section className="mb-8 rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Расход на одну плиту</h2>
            {data.consumptionPerUnit.pieces === 0 ? (
              <p className="text-base text-dim">Плит за период не выпускали</p>
            ) : (
              <ul className="flex flex-col gap-2 text-base">
                {data.consumptionPerUnit.materials.map((row) => (
                  <li key={row.code} className="flex justify-between">
                    <span className="text-body">{row.name}</span>
                    <span className="tabular-nums">{row.perPieceG ?? '—'} г</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mb-8 rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Надёжность сотрудников</h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-base">
                <thead className="text-dim">
                  <tr>
                    <th className="py-1">Сотрудник</th>
                    <th className="py-1">Смен</th>
                    <th className="py-1">Отгулы</th>
                    <th className="py-1">Срывы</th>
                    <th className="py-1">Не сдал</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reliability.map((row) => (
                    <tr key={row.userId}>
                      <td className="py-1">{row.fullName}</td>
                      <td className="py-1 tabular-nums">{row.shifts}</td>
                      <td className="py-1 tabular-nums">{row.plannedAbsences}</td>
                      <td
                        className={`py-1 tabular-nums ${row.unplannedAbsences > 0 ? 'text-red-300' : ''}`}
                      >
                        {row.unplannedAbsences}
                      </td>
                      <td
                        className={`py-1 tabular-nums ${row.missedReports > 0 ? 'text-amber-700' : ''}`}
                      >
                        {row.missedReports}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl bg-card shadow-card p-4">
            <h2 className="mb-3 text-lg font-semibold">Выгрузки в Excel</h2>
            <div className="flex flex-wrap gap-touch">
              {EXPORTS.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => void download(item.path, item.title)}
                  className="min-h-touch rounded-xl bg-muted px-5 text-base font-semibold active:bg-line"
                >
                  {item.title}
                </button>
              ))}
            </div>
            <p className="mt-3 text-base text-dim">
              Выгружается выбранный период. В файле — заголовки, автоширина колонок и итоговая
              строка.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
